import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["visualizationSettingsString", "visualizationSettingsMap"];
import { denormalizeVizType } from 'crm-index-visualization-core/core/model/vizSetting';

// Symbol to mark FE-added fields
const FE_ADDED = 'fe-added';
const VIZ_SETTING_STORAGE_PREFIX = 'interimAPI-vizsetting';

/**
 * Marks a field as frontend-added using the FE_ADDED symbol.
 * Used to track which fields should be stripped before backend saves.
 */
export function markAsFeAdded(obj) {
  return Object.assign({}, obj, {
    [FE_ADDED]: true
  });
}

/**
 * Checks if an object or field was added by the frontend.
 */
export function isFeAdded(obj) {
  return typeof obj === 'object' && obj !== null && FE_ADDED in obj;
}

// Interim response types (to be removed when BE is ready)

export function isInterimView(view) {
  return 'visualizationSettingsString' in view;
}

/**
 * Adapts backend view to include visualization settings for interim compatibility
 * @param view - Backend view to adapt
 * @param vizSetting - VizSetting to add to the view
 * @returns Enhanced view with visualizationSettingsString
 * @throws Never throws - safely handles missing fields
 * @example adapt(view, new VizSetting('BOARD', {})) // Adds BOARD settings
 */
export function adapt(view, vizSetting) {
  // Add viz settings for interim compatibility
  const enhanced = Object.assign({}, view, {
    currentViewVisualization: vizSetting.visualizationType,
    visualizationSettingsString: JSON.stringify(markAsFeAdded({
      id: vizSetting.id,
      visualizationType: vizSetting.visualizationType,
      settings: vizSetting.settings
    }))
  });
  return enhanced;
}

/**
 * Removes frontend-added fields from an adapted view to make it backend-compatible
 * Strips FE-only fields like visualizationSettingsString, currentViewVisualization, and visualizationSettingsMap
 * @param view - Adapted view with FE-added fields
 * @returns Clean backend view ready for API calls
 * @example unadapt(adaptedView) // Removes FE fields, returns clean backend view
 */
export function unadapt(view) {
  if (!isInterimView(view)) {
    return view;
  }

  // Strip FE-added fields
  const _ref = view,
    cleanView = _objectWithoutPropertiesLoose(_ref, _excluded);
  cleanView.currentViewVisualization = denormalizeVizType(cleanView.currentViewVisualization);
  return cleanView;
}

/**
 * Get a temporary storage key for a viz setting so that API layer can read viz settings from and write to client storage whilst BE is not ready.
 * @param vizType - The viz type to get the key for
 * @param viewId - The view id to get the key for
 * @param objectTypeId - The object type id to get the key for
 * @returns The temporary storage key
 */
export function getTemptStorageKey(vizType, viewId, objectTypeId) {
  return `${VIZ_SETTING_STORAGE_PREFIX}-${vizType}-${viewId}-${objectTypeId}`;
}